<template>
  <section id="contact" class="py-20 lg:py-32 bg-slate-950">
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <!-- Section Header -->
      <div class="text-center mb-16 animate-fade-in-up">
        <h2 class="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-gold-gradient mb-4">
          تواصل معنا
        </h2>
        <div class="w-20 h-1 bg-gold-gradient mx-auto mb-6"></div>
        <p class="text-gray-300 text-lg max-w-2xl mx-auto">
          قم بزيارة صالة العرض الخاصة بنا أو تواصل معنا. نحن هنا لمساعدتك في العثور على القطعة المثالية.
        </p>
      </div>

      <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        <!-- Contact Information -->
        <div class="space-y-8 animate-fade-in-up">
          <!-- Contact Details -->
          <div class="space-y-6">
            <div class="flex items-start space-x-4 space-x-reverse">
              <div class="flex-shrink-0 w-12 h-12 bg-gold-500/20 rounded-lg flex items-center justify-center">
                <MapPin class="text-gold-500" :size="24" />
              </div>
              <div>
                <h3 class="text-xl font-display font-semibold text-gold-400 mb-2">زرنا</h3>
                <a href="https://maps.app.goo.gl/ifmAFRxNvAAAYU8w9" target="_blank" rel="noopener noreferrer"
                  class="text-gray-300 hover:text-gold-400 transition-smooth">
                  شارع الصادق فرع معرض الهدايا قرب صيدلية سلوى<br />النجف الاشرف، المدينة القديمة
                </a>
              </div>
            </div>

            <div class="flex items-start space-x-4 space-x-reverse">
              <div class="flex-shrink-0 w-12 h-12 bg-gold-500/20 rounded-lg flex items-center justify-center">
                <Phone class="text-gold-500" :size="24" />
              </div>
              <div>
                <h3 class="text-xl font-display font-semibold text-gold-400 mb-2">اتصل بنا</h3>
                <p class="text-gray-300">
                  <a href="tel:+9647801213504" class="hover:text-gold-400 transition-smooth">7801213504 964+</a> ||
                  <a href="tel:+964786622128" class="hover:text-gold-400 transition-smooth">786622128 964+</a>
                  <br />السبت - الخميس: 9 صباحاً - 9 مساءً
                </p>
              </div>
            </div>

            <div class="flex items-start space-x-4 space-x-reverse">
              <div class="flex-shrink-0 w-12 h-12 bg-gold-500/20 rounded-lg flex items-center justify-center">
                <Mail class="text-gold-500" :size="24" />
              </div>
              <div>
                <h3 class="text-xl font-display font-semibold text-gold-400 mb-2">راسلنا</h3>
                <p class="text-gray-300">
                  <a href="mailto:mmohh.lith21@gmail.com" class="hover:text-gold-400 transition-smooth">
                    mmohh.lith21@gmail.com
                  </a>
                </p>
              </div>
            </div>
          </div>

          <!-- Social Media -->
          <div>
            <h3 class="text-xl font-display font-semibold text-gold-400 mb-4">تابعنا</h3>
            <div class="flex space-x-4 space-x-reverse">
              <a href="https://www.instagram.com/fm.gold_qasar?utm_source=qr&igsh=d3Nmcm94NnJwZDNy" target="_blank"
                rel="noopener noreferrer"
                class="w-12 h-12 bg-gold-500/20 hover:bg-gold-500 rounded-lg flex items-center justify-center transition-smooth group"
                aria-label="Instagram">
                <Instagram class="text-gold-500 group-hover:text-richBlack transition-smooth" :size="20" />
              </a>
              <a href="https://www.tiktok.com/@fras.ali86?_r=1&_t=ZS-91r8N4XYyG5" target="_blank"
                rel="noopener noreferrer"
                class="w-12 h-12 bg-gold-500/20 hover:bg-gold-500 rounded-lg flex items-center justify-center transition-smooth group"
                aria-label="TikTok">
                <TiktokIcon :size="20" class-name="text-gold-500 group-hover:text-richBlack transition-smooth" />
              </a>
            </div>
          </div>

          <!-- Google Maps -->
          <div class="relative h-64 rounded-xl overflow-hidden border border-gold-500/20">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3376.8!2d44.3!3d32.0!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMzLCsDAwJzAwLjAiTiA0NMKwMTgnMDAuMCJF!5e0!3m2!1sen!2siq!4v1234567890123!5m2!1sen!2siq"
              width="100%" height="100%" style="border:0;" allowfullscreen="" loading="lazy"
              referrerpolicy="no-referrer-when-downgrade" class="w-full h-full"></iframe>
          </div>
        </div>

        <!-- Contact Form -->
        <div class="animate-fade-in-up" style="animation-delay: 0.2s;">
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div>
              <label for="name" class="block text-gray-300 font-medium mb-2">اسمك</label>
              <input type="text" id="name" v-model="formData.name" required class="input-gold w-full"
                placeholder="أحمد محمد" />
            </div>

            <div>
              <label for="email" class="block text-gray-300 font-medium mb-2">البريد الإلكتروني</label>
              <input type="email" id="email" v-model="formData.email" required class="input-gold w-full"
                placeholder="ahmed@example.com" />
            </div>

            <div>
              <label for="phone" class="block text-gray-300 font-medium mb-2">رقم الهاتف</label>
              <input type="tel" id="phone" v-model="formData.phone" class="input-gold w-full" style="text-align: right;"
                placeholder="078049XXXXX" />
            </div>

            <div>
              <label for="message" class="block text-gray-300 font-medium mb-2">الرسالة</label>
              <textarea id="message" v-model="formData.message" required rows="5" class="input-gold w-full resize-none"
                placeholder="أخبرنا كيف يمكننا مساعدتك..."></textarea>
            </div>

            <button type="submit" class="btn-gold w-full">
              إرسال الرسالة
            </button>
          </form>

          <!-- Success Message -->
          <Transition name="fade">
            <div v-if="showSuccess"
              class="mt-6 p-4 bg-gold-500/20 border border-gold-500/40 rounded-lg text-gold-400 text-center">
              شكراً لك! سنعاود الاتصال بك قريباً.
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { ref } from 'vue'
import { MapPin, Phone, Mail, Instagram } from 'lucide-vue-next'
import TiktokIcon from './icons/TiktokIcon.vue'

// Form data
const formData = ref({
  name: '',
  email: '',
  phone: '',
  message: ''
})

const showSuccess = ref(false)

/**
 * Handle form submission
 */
const handleSubmit = () => {
  // In a real application, this would send data to a backend
  console.log('Form submitted:', formData.value)

  // Show success message
  showSuccess.value = true

  // Reset form
  formData.value = {
    name: '',
    email: '',
    phone: '',
    message: ''
  }

  // Hide success message after 5 seconds
  setTimeout(() => {
    showSuccess.value = false
  }, 5000)
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
